/*
 * Copyright (c) 2026, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you've
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import { Box, Typography } from '@material-ui/core';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { useRef, useState } from 'react';
import Button from 'components/ChoreoSystem/Button/Button';
import Card from 'components/ChoreoSystem/Card/Card';
import CardContent from 'components/ChoreoSystem/Card/CardContent';
import Tooltip from 'components/ChoreoSystem/Tooltip/Tooltip';
import { ArrowRightShort } from 'components/ChoreoSystem/Icons/generated';
import NotSupportedEngineMessage from 'modules/login/NotSupportedEngineMessage';
import CopilotPromptBox, {
  useExamplePrompts,
} from 'modules/aiCopilot/CopilotPromptBox';
import type {
  CopilotPromptBoxHandle,
  ExamplePromptMessages,
} from 'modules/aiCopilot/CopilotPromptBox';
import PublicHeader from 'modules/login/PublicHeader';
import PublicFooter from 'modules/login/PublicFooter';
import PrebuiltIntegrationFlows from 'modules/login/PrebuiltIntegrationFlows';
import type { PrebuiltIntegrationFlow } from 'modules/login/PrebuiltIntegrationFlows';
import { MAX_PROMPT_LENGTH } from 'utils/copilotPrompt';
import LandingValueProps from 'modules/login/LandingValueProps';
import useStyles from './CopilotLanding.styles';

interface CopilotLandingProps {
  /** Called with the trimmed prompt when the user submits it. */
  onPromptSubmit: (prompt: string) => void;
  onLoginClick: () => void;
  onSignUpClick: () => void;
  /** Called when the visitor would rather browse than describe something. */
  onExploreIntegrationsClick: () => void;
  /**
   * Called with a prebuilt integration picked from the row below the prompt.
   * The choice is carried through sign up and applied on the catalogue page.
   */
  onPrebuiltIntegrationClick: (flow: PrebuiltIntegrationFlow) => void;
  isSupportedEngine?: boolean;
}

/**
 * The examples offered under the prompt box.
 *
 * Deliberately not the create page's `DEFAULT_EXAMPLE_PROMPT_MESSAGES`: those sell
 * specific integrations to someone who already signed up to build one. A
 * visitor landing here may not know what the platform is, so the first of
 * these asks exactly that.
 */
const LANDING_EXAMPLE_PROMPT_MESSAGES: ExamplePromptMessages[] = [
  {
    short: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.platformOverview.short',
      defaultMessage: 'What can I do on this platform',
    }),
    full: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.platformOverview.full',
      defaultMessage: 'Explain what I can do on the WSO2 Integration Platform.',
    }),
  },
  {
    short: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.slackNotifications.short',
      defaultMessage: 'Get Slack notifications',
    }),
    full: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.slackNotifications.full',
      defaultMessage:
        'Send me a Slack notification when a new PR is opened in my GitHub' +
        ' repo.',
    }),
  },
  {
    short: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.deployIntegration.short',
      defaultMessage: 'Deploy my integration',
    }),
    full: defineMessage({
      id: 'modules.login.copilotLanding.examplePrompts.deployIntegration.full',
      defaultMessage:
        'How can I build and deploy my integration from the source in GitHub.',
    }),
  },
];

/**
 * The prompt card, holding the draft it is being typed into.
 *
 * The state lives down here rather than on the landing page so a keystroke
 * re-renders this card alone. With it one level up, every character typed also
 * re-rendered the header, the hero, the connector strip and the Explore button
 * — which is what made the input feel unresponsive.
 *
 * It survives the auth dialog opening and closing either way: the dialog is a
 * sibling of the landing page, so nothing here unmounts.
 *
 * The examples sit under the card rather than inside the box — they are the
 * page's invitation to a visitor with nothing to type, and the box itself is
 * kept short here. They stay in this component because picking one writes the
 * same draft the box is bound to.
 */
function LandingPromptCard({
  onPromptSubmit,
}: Pick<CopilotLandingProps, 'onPromptSubmit'>) {
  const classes = useStyles();
  const intl = useIntl();
  const examplePrompts = useExamplePrompts(LANDING_EXAMPLE_PROMPT_MESSAGES);
  const [promptDraft, setPromptDraft] = useState('');
  const promptBoxRef = useRef<CopilotPromptBoxHandle>(null);

  // Picking an example writes the draft and then puts the caret after it, the
  // way the chips inside the box do. The chips here sit after the box in the
  // document, so without the focus a keyboard visitor is left standing on the
  // chip with the text they just chose behind them.
  const handleExamplePromptClick = (fullPrompt: string) => {
    setPromptDraft(fullPrompt);
    promptBoxRef.current?.focus();
  };

  return (
    <>
      <Box className={classes.promptCardWrapper}>
        <Card
          testId="copilot-landing-chatbox"
          boxShadow="none"
          borderRadius="md"
          className={classes.promptCard}
        >
          <CardContent>
            <CopilotPromptBox
              ref={promptBoxRef}
              value={promptDraft}
              onChange={setPromptDraft}
              onSubmit={onPromptSubmit}
              // Shorter than on the create page. There the box is the whole
              // point of the screen; here it shares the page with the hero copy,
              // the connector strip and the Explore button, and at full height
              // it pushed the strip below the fold on a laptop.
              size="small"
              showExamplePrompts={false}
              // The longest prompt Copilot accepts, and also the length this
              // prompt is truncated to while it is parked in `localStorage`
              // across sign in.
              maxLength={MAX_PROMPT_LENGTH}
              submitTestId="copilot-landing-submit"
              // Formatted here rather than handed over as a node: the button
              // is icon only, so this is its accessible name and its tooltip.
              submitLabel={intl.formatMessage({
                id: 'modules.login.copilotLanding.submitButton',
                defaultMessage: 'Start planning',
              })}
            />
          </CardContent>
        </Card>
      </Box>

      <Box className={classes.examplePromptsSection}>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.examplePromptsCaption}
        >
          <FormattedMessage
            id="modules.login.copilotLanding.examplePromptsCaption"
            defaultMessage="Example prompts to try"
          />
        </Typography>
        <Box className={classes.examplePromptsRow}>
          {examplePrompts.map((prompt) => (
            // The chip is short and the prompt it writes is not, so the
            // tooltip is what tells the visitor which sentence they are about
            // to put in the box.
            <Tooltip key={prompt.short} title={prompt.full} placement="top">
              <button
                type="button"
                className={classes.examplePromptChip}
                onClick={() => handleExamplePromptClick(prompt.full)}
              >
                {prompt.short}
              </button>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </>
  );
}

/**
 * The unauthenticated landing page: a header with the two auth entry points and
 * the Copilot prompt as the only content.
 *
 * The prompt is the primary call to action here — submitting it opens the auth
 * dialog, so a visitor describes what they want to build before being asked to
 * sign in.
 */
export default function CopilotLanding(props: CopilotLandingProps) {
  const {
    onPromptSubmit,
    onLoginClick,
    onSignUpClick,
    onExploreIntegrationsClick,
    onPrebuiltIntegrationClick,
    // Defaulted rather than left `undefined`: the banner below renders when
    // this is falsy, so an omitted flag must not be read as "unsupported".
    isSupportedEngine = true,
  } = props;
  const classes = useStyles();

  return (
    <Box className={classes.landingRoot}>
      <Box className={classes.heroScreen}>
        <PublicHeader
          onLoginClick={onLoginClick}
          onSignUpClick={onSignUpClick}
        />

        <Box component="main" className={classes.landingMain}>
          <Box className={classes.landingContent}>
            <Box mb={1} textAlign="center">
              <Typography variant="h2" className={classes.heroTitle}>
                <FormattedMessage
                  id="modules.login.copilotLanding.title"
                  defaultMessage="What do you want to create?"
                />
              </Typography>
            </Box>
            <Box mb={4} textAlign="center">
              <Typography variant="h4" color="textSecondary">
                <FormattedMessage
                  id="modules.login.copilotLanding.description"
                  defaultMessage="Ask a question or describe an idea. WSO2 Integrator 
                  Copilot explains, plans, and builds it with you."
                />
              </Typography>
            </Box>

            <LandingPromptCard onPromptSubmit={onPromptSubmit} />
          </Box>

          <Box className={classes.prebuiltSection}>
            {/*
              The row below is a sampler of the catalogue, not the catalogue.
              The caption opens the section by saying what pressing a chip
              does, and the button closing the same line is the way through to
              the full list — the way out for a visitor who neither knows what
              to type nor sees their apps drift past.
            */}
            <Box className={classes.prebuiltHeader}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="modules.login.copilotLanding.prebuiltIntegrationsCaption"
                  defaultMessage="Or start from a prebuilt integration"
                />
              </Typography>
              <Button
                testId="copilot-landing-explore-integrations"
                variant="text"
                color="primary"
                endIcon={<ArrowRightShort className={classes.exploreIcon} />}
                onClick={onExploreIntegrationsClick}
              >
                <FormattedMessage
                  id="modules.login.copilotLanding.exploreIntegrations"
                  defaultMessage="Explore Integrations"
                />
              </Button>
            </Box>

            <PrebuiltIntegrationFlows
              onIntegrationClick={onPrebuiltIntegrationClick}
            />
          </Box>

          {!isSupportedEngine && <NotSupportedEngineMessage />}
        </Box>
      </Box>

      <Box component="section" className={classes.bannerSection}>
        <Box mb={1}>
          <Typography variant="h2" className={classes.bannerTitle}>
            <FormattedMessage
              id="modules.login.copilotLanding.bannerTitle"
              defaultMessage="Everything You Need to Build, Connect, and Scale"
            />
          </Typography>
        </Box>
        {/*
          `component="p"`: `h4` is the right size here, but the line is a
          sub-heading for the title above it, not a heading of its own.
        */}
        <Typography
          variant="h4"
          component="p"
          className={classes.bannerSubtitle}
        >
          <FormattedMessage
            id="modules.login.copilotLanding.bannerSubtitle"
            defaultMessage="The AI-native integration platform built for modern teams."
          />
        </Typography>
      </Box>

      <Box component="section" className={classes.valuePropsSection}>
        <LandingValueProps />
      </Box>

      <PublicFooter />
    </Box>
  );
}
